import { ReactElement, useMemo } from 'react'
import { buildErrorMessage } from '@constants'
import { AxiosError } from 'axios'
import { Button } from '@mui/material'
import type { ProjectFieldDTO, ProjectFieldWriteDTO } from '@api/fastAPI.schemas'
import {
  useCreateOrganisationProjectField,
  useUpdateOrganisationProjectField,
} from '@api/organisation-project-fields/organisation-project-fields'
import { SaveButton } from '@ui/actions'
import { DialogBase } from '@ui/feedback'
import { Form2 } from '@ui/forms'
import { useSnackbar } from '@ui/snackbar'
import useInvalidateProjectFields from '../hooks/useInvalidateProjectFields'
import { ProjectFieldFormValues, buildProjectFieldSchema } from '../schema'
import { normalizeTitle, templateKey } from '../utils'
import ProjectFieldFormFields from './ProjectFieldFormFields'

interface Props {
  /** The field to edit; a new one is created when absent. */
  field?: ProjectFieldDTO
  /** All fields of the organisation, to keep titles unique. */
  fields: ProjectFieldDTO[]
  onClose: () => void
}

const FORM_ID = 'project-field-form'

const toWriteDTO = (values: ProjectFieldFormValues): ProjectFieldWriteDTO => ({
  title: normalizeTitle(values.title),
  description: values.description.trim(),
  field_type: values.field_type,
  options:
    values.field_type === 'select'
      ? values.options.map(option => ({
          id: option.id || null,
          label: option.label.trim(),
          value: option.value.trim(),
        }))
      : [],
})

const ProjectFieldDialog = ({ field, fields, onClose }: Props): ReactElement => {
  const { enqueueSnackbar } = useSnackbar()
  const invalidate = useInvalidateProjectFields()

  const validationSchema = useMemo(
    () =>
      buildProjectFieldSchema(
        fields
          .filter(other => other.guid !== field?.guid)
          .map(other => templateKey(other.title).toLowerCase()),
      ),
    [fields, field?.guid],
  )

  const callbacks = (successMessage: string) => ({
    onSuccess: async () => {
      await invalidate()
      enqueueSnackbar(successMessage, { variant: 'success' })
      onClose()
    },
    onError: (error: AxiosError) => {
      enqueueSnackbar(buildErrorMessage(error, 'Fehler beim Speichern des Projektdatenfelds'), {
        variant: 'error',
      })
    },
  })

  const { mutate: createField, isLoading: isCreating } = useCreateOrganisationProjectField<
    AxiosError,
    unknown
  >({
    mutation: {
      meta: {
        title: 'Projektdatenfeld hinzufügen',
        description: 'Fügt den Projekten der Organisation ein Projektdatenfeld hinzu',
      },
      ...callbacks('Projektdatenfeld hinzugefügt'),
    },
  })

  const { mutate: updateField, isLoading: isUpdating } = useUpdateOrganisationProjectField<
    AxiosError,
    unknown
  >({
    mutation: {
      meta: {
        title: 'Projektdatenfeld bearbeiten',
        description: 'Ändert ein Projektdatenfeld der Organisation',
      },
      ...callbacks('Projektdatenfeld gespeichert'),
    },
  })

  const defaultValues: ProjectFieldFormValues = field
    ? {
        field_type: field.field_type,
        title: field.title,
        description: field.description,
        options: field.options.map(option => ({
          id: option.id,
          label: option.label,
          value: option.value,
        })),
      }
    : { field_type: 'text', title: '', description: '', options: [] }

  return (
    <DialogBase
      open
      onClose={onClose}
      title={field ? 'Projektdatenfeld bearbeiten' : 'Projektdatenfeld hinzufügen'}
      dialogProps={{ maxWidth: 'md', fullWidth: true }}
    >
      <Form2<ProjectFieldFormValues>
        id={FORM_ID}
        data-cy="project-field-form"
        defaultValues={defaultValues}
        validationSchema={validationSchema}
        onSubmit={values =>
          field
            ? updateField({ fieldGuid: field.guid, data: toWriteDTO(values) })
            : createField({ data: toWriteDTO(values) })
        }
      >
        <ProjectFieldFormFields isEdit={!!field} />
      </Form2>
      <>
        <Button size="small" onClick={onClose}>
          Abbrechen
        </Button>
        <SaveButton
          size="small"
          loading={isCreating || isUpdating}
          form={FORM_ID}
          type="submit"
          data-cy="btn-save-project-field"
        >
          Speichern
        </SaveButton>
      </>
    </DialogBase>
  )
}

export default ProjectFieldDialog
